import {
    useEffect,
    useMemo,
    useReducer,
    useRef,
} from 'react';
import { useViewerBinaryCache } from '../../viewer-cache';
import { buildRenderableHandle } from './renderables';
import {
    areRenderStatusMapsEqual,
    buildNextRenderStatuses,
    buildRuntimeModelStateById,
    getInactiveRenderStatus,
    getIsSceneLoading,
} from './sceneRenderableState';
import { getAppearanceForModel } from './sceneViewportUtils';

const INITIAL_RENDERABLE_METADATA_STATE = {
    handleCapabilitiesByModelId: {},
    renderStatuses: {},
    visibleBoundsVersion: 0,
};

function invalidateRenderableResources(viewerBinaryCache, invalidatedResources) {
    if (!Array.isArray(invalidatedResources)) {
        return;
    }

    invalidatedResources.forEach((resourceRef) => {
        viewerBinaryCache.invalidate(resourceRef);
    });
}

function doesRenderableEntryMatchAsset(entry, model, asset) {
    return Boolean(entry)
        && Boolean(asset)
        && entry.descriptorKey === model.descriptorKey
        && entry.geometryBuffer === asset.geometryBuffer
        && entry.textureBlob === asset.textureBlob;
}

function isRenderableActiveModel(visibleModelIdsRef, model) {
    return model.shouldBuild && visibleModelIdsRef.current.has(model.id);
}

function doHandleCapabilitiesMatch(currentCapabilities, nextCapabilities) {
    return (currentCapabilities?.supportsColor ?? null) === (nextCapabilities?.supportsColor ?? null)
        && (currentCapabilities?.supportsTexture ?? null) === (nextCapabilities?.supportsTexture ?? null);
}

function sceneRenderablesReducer(state, action) {
    switch (action.type) {
        case 'set-render-status': {
            if (state.renderStatuses[action.modelId] === action.status) {
                return state;
            }

            return {
                ...state,
                renderStatuses: {
                    ...state.renderStatuses,
                    [action.modelId]: action.status,
                },
            };
        }

        case 'attach-renderable': {
            const currentCapabilities = state.handleCapabilitiesByModelId[action.modelId];
            const capabilitiesUnchanged = doHandleCapabilitiesMatch(
                currentCapabilities,
                action.capabilities,
            );
            const nextCapabilitiesByModelId = capabilitiesUnchanged
                ? state.handleCapabilitiesByModelId
                : {
                    ...state.handleCapabilitiesByModelId,
                    [action.modelId]: action.capabilities || {},
                };
            const nextRenderStatuses = state.renderStatuses[action.modelId] === 'ready'
                ? state.renderStatuses
                : {
                    ...state.renderStatuses,
                    [action.modelId]: 'ready',
                };

            return {
                ...state,
                handleCapabilitiesByModelId: nextCapabilitiesByModelId,
                renderStatuses: nextRenderStatuses,
                visibleBoundsVersion: action.affectsVisibleBounds
                    ? state.visibleBoundsVersion + 1
                    : state.visibleBoundsVersion,
            };
        }

        case 'remove-renderable': {
            const hasCapabilities = state.handleCapabilitiesByModelId[action.modelId] !== undefined;
            const nextCapabilitiesByModelId = hasCapabilities
                ? { ...state.handleCapabilitiesByModelId }
                : state.handleCapabilitiesByModelId;

            if (hasCapabilities) {
                delete nextCapabilitiesByModelId[action.modelId];
            }

            let nextRenderStatuses = state.renderStatuses;

            if (
                action.nextRenderStatus
            && state.renderStatuses[action.modelId] !== action.nextRenderStatus
            ) {
                nextRenderStatuses = {
                    ...state.renderStatuses,
                    [action.modelId]: action.nextRenderStatus,
                };
            }

            if (
                !hasCapabilities
            && nextRenderStatuses === state.renderStatuses
            && !action.affectsVisibleBounds
            ) {
                return state;
            }

            return {
                ...state,
                handleCapabilitiesByModelId: nextCapabilitiesByModelId,
                renderStatuses: nextRenderStatuses,
                visibleBoundsVersion: action.affectsVisibleBounds
                    ? state.visibleBoundsVersion + 1
                    : state.visibleBoundsVersion,
            };
        }

        case 'prune-handle-capabilities': {
            const nextCapabilitiesByModelId = Object.fromEntries(
                Object.entries(state.handleCapabilitiesByModelId)
                    .filter(([modelId]) => action.sceneModelIdSet.has(modelId)),
            );

            return Object.keys(nextCapabilitiesByModelId).length
            === Object.keys(state.handleCapabilitiesByModelId).length
                ? state
                : {
                    ...state,
                    handleCapabilitiesByModelId: nextCapabilitiesByModelId,
                };
        }

        case 'reconcile-render-statuses': {
            const nextRenderStatuses = buildNextRenderStatuses(
                state.renderStatuses,
                action.sceneModels,
                action.visibleModelIdSet,
            );

            return areRenderStatusMapsEqual(state.renderStatuses, nextRenderStatuses)
                ? state
                : {
                    ...state,
                    renderStatuses: nextRenderStatuses,
                };
        }

        case 'bump-visible-bounds-version':
            return {
                ...state,
                visibleBoundsVersion: state.visibleBoundsVersion + 1,
            };

        default:
            return state;
    }
}

function dispatchRenderStatus(dispatch, modelId, status) {
    dispatch({
        type: 'set-render-status',
        modelId,
        status,
    });
}

function dispatchAttachRenderable(dispatch, {
    affectsVisibleBounds,
    capabilities,
    modelId,
}) {
    dispatch({
        type: 'attach-renderable',
        affectsVisibleBounds,
        capabilities,
        modelId,
    });
}

function dispatchRemoveRenderable(dispatch, {
    affectsVisibleBounds,
    modelId,
    nextRenderStatus,
}) {
    dispatch({
        type: 'remove-renderable',
        affectsVisibleBounds,
        modelId,
        nextRenderStatus,
    });
}

function dispatchPruneHandleCapabilities(dispatch, sceneModelIdSet) {
    dispatch({
        type: 'prune-handle-capabilities',
        sceneModelIdSet,
    });
}

function dispatchReconcileRenderStatuses(dispatch, sceneModels, visibleModelIdSet) {
    dispatch({
        type: 'reconcile-render-statuses',
        sceneModels,
        visibleModelIdSet,
    });
}

function removeRenderHandle({
    affectsVisibleBounds,
    dispatch,
    modelId,
    nextRenderStatus,
    renderHandlesRef,
    stageRef,
}) {
    const existingEntry = renderHandlesRef.current.get(modelId);

    if (!existingEntry) {
        return;
    }

    if (stageRef.current) {
        stageRef.current.remove(existingEntry.handle.object3D);
    }

    existingEntry.handle.dispose();
    renderHandlesRef.current.delete(modelId);
    dispatchRemoveRenderable(dispatch, {
        affectsVisibleBounds,
        modelId,
        nextRenderStatus,
    });
}

function useSceneRenderables({
    appearance,
    assetsById,
    isViewportReady,
    sceneModels,
    stageRef,
    visibleModelIds,
}) {
    const viewerBinaryCache = useViewerBinaryCache();
    const buildVersionRef = useRef(0);
    const renderHandlesRef = useRef(new Map());
    const visibleModelIdsKey = useMemo(
        () => visibleModelIds.join('|'),
        [visibleModelIds],
    );
    const visibleModelIdSet = useMemo(
        () => new Set(visibleModelIds),
        [visibleModelIdsKey],
    );
    const visibleModelIdsRef = useRef(visibleModelIdSet);
    const appearanceRef = useRef(appearance);
    const [renderableMetadata, dispatch] = useReducer(
        sceneRenderablesReducer,
        INITIAL_RENDERABLE_METADATA_STATE,
    );
    const {
        handleCapabilitiesByModelId,
        renderStatuses,
        visibleBoundsVersion,
    } = renderableMetadata;
    const sceneModelIdSet = useMemo(
        () => new Set(sceneModels.map((model) => model.id)),
        [sceneModels],
    );
    const buildableSceneModels = useMemo(
        () => sceneModels.filter((model) => model.shouldBuild && visibleModelIdSet.has(model.id)),
        [sceneModels, visibleModelIdSet],
    );
    const isSceneLoading = useMemo(
        () => getIsSceneLoading(buildableSceneModels, renderStatuses),
        [buildableSceneModels, renderStatuses],
    );
    const runtimeModelStateById = useMemo(
        () => buildRuntimeModelStateById({
            assetsById,
            handleCapabilitiesByModelId,
            renderStatuses,
            sceneModels,
            visibleModelIdSet,
        }),
        [assetsById, handleCapabilitiesByModelId, renderStatuses, sceneModels, visibleModelIdSet],
    );

    useEffect(() => {
        visibleModelIdsRef.current = visibleModelIdSet;

        let visibilityChanged = false;

        renderHandlesRef.current.forEach((entry, modelId) => {
            const shouldBeVisible = visibleModelIdSet.has(modelId);

            if (entry.handle.object3D.visible !== shouldBeVisible) {
                visibilityChanged = true;
            }

            entry.handle.setVisible(shouldBeVisible);
        });

        if (visibilityChanged) {
            dispatch({
                type: 'bump-visible-bounds-version',
            });
        }
    }, [visibleModelIdSet]);

    useEffect(() => {
        appearanceRef.current = appearance;

        renderHandlesRef.current.forEach((entry, modelId) => {
            entry.handle.applyAppearance(getAppearanceForModel(appearance, modelId));
        });
    }, [appearance]);

    useEffect(() => {
        dispatchPruneHandleCapabilities(dispatch, sceneModelIdSet);
    }, [sceneModelIdSet]);

    useEffect(() => {
        dispatchReconcileRenderStatuses(dispatch, sceneModels, visibleModelIdSet);
    }, [sceneModels, visibleModelIdSet]);

    useEffect(() => () => {
        buildVersionRef.current += 1;
        renderHandlesRef.current.forEach((entry) => {
            entry.handle.dispose();
        });
        renderHandlesRef.current.clear();
    }, []);

    useEffect(() => {
        if (!isViewportReady || !stageRef.current) {
            return undefined;
        }

        const pruneInactiveRenderHandles = () => {
            const activeModelIds = new Set(
                sceneModels
                    .filter((model) => isRenderableActiveModel(visibleModelIdsRef, model))
                    .map((model) => model.id),
            );

            renderHandlesRef.current.forEach((entry, modelId) => {
                if (!activeModelIds.has(modelId)) {
                    removeRenderHandle({
                        affectsVisibleBounds: entry.handle.object3D.visible,
                        dispatch,
                        modelId,
                        nextRenderStatus: null,
                        renderHandlesRef,
                        stageRef,
                    });
                }
            });
        };

        const syncExistingRenderable = (model, asset, currentEntry) => {
            if (!doesRenderableEntryMatchAsset(currentEntry, model, asset)) {
                return false;
            }

            currentEntry.handle.applyAppearance(
                getAppearanceForModel(appearanceRef.current, model.id),
            );
            currentEntry.handle.setVisible(true);
            dispatchRenderStatus(dispatch, model.id, 'ready');
            return true;
        };

        const attachBuiltRenderable = (model, asset, handle) => {
            const isVisible = visibleModelIdsRef.current.has(model.id);

            invalidateRenderableResources(viewerBinaryCache, handle.invalidatedResources);
            handle.applyAppearance(getAppearanceForModel(appearanceRef.current, model.id));
            handle.setVisible(isVisible);
            stageRef.current?.add(handle.object3D);

            renderHandlesRef.current.set(model.id, {
                descriptorKey: model.descriptorKey,
                geometryBuffer: asset.geometryBuffer,
                handle,
                textureBlob: asset.textureBlob,
            });
            dispatchAttachRenderable(dispatch, {
                affectsVisibleBounds: isVisible,
                capabilities: handle.capabilities,
                modelId: model.id,
            });
        };

        const startRenderableBuild = (model, asset, buildVersion, isCancelled) => {
            dispatchRenderStatus(dispatch, model.id, 'building');

            buildRenderableHandle(model, asset, appearanceRef.current)
                .then((handle) => {
                    if (!handle) {
                        if (!isCancelled() && buildVersion === buildVersionRef.current) {
                            dispatchRenderStatus(dispatch, model.id, 'failed');
                        }
                        return;
                    }

                    if (isCancelled() || buildVersion !== buildVersionRef.current) {
                        handle.dispose();
                        return;
                    }

                    attachBuiltRenderable(model, asset, handle);
                })
                .catch((error) => {
                    if (isCancelled() || buildVersion !== buildVersionRef.current) {
                        return;
                    }

                    invalidateRenderableResources(viewerBinaryCache, error?.invalidatedResources);
                    console.error(`Failed to build ${model.format.toUpperCase()} renderable.`, error);
                    dispatchRenderStatus(dispatch, model.id, 'failed');
                });
        };

        const reconcileModelRenderable = (model, buildVersion, isCancelled) => {
            const asset = assetsById[model.id];
            const assetMatchesModel = asset?.descriptorKey === model.descriptorKey;
            const existingEntry = renderHandlesRef.current.get(model.id);
            const isRenderableActive = isRenderableActiveModel(visibleModelIdsRef, model);

            if (existingEntry && !doesRenderableEntryMatchAsset(existingEntry, model, asset)) {
                removeRenderHandle({
                    affectsVisibleBounds: existingEntry.handle.object3D.visible,
                    dispatch,
                    modelId: model.id,
                    nextRenderStatus: null,
                    renderHandlesRef,
                    stageRef,
                });
            }

            if (!asset || !assetMatchesModel) {
                dispatchRenderStatus(
                    dispatch,
                    model.id,
                    isRenderableActive ? 'loading' : 'idle',
                );
                return;
            }

            if (!isRenderableActive) {
                const currentEntry = renderHandlesRef.current.get(model.id);

                if (currentEntry) {
                    removeRenderHandle({
                        affectsVisibleBounds: currentEntry.handle.object3D.visible,
                        dispatch,
                        modelId: model.id,
                        nextRenderStatus: getInactiveRenderStatus(asset),
                        renderHandlesRef,
                        stageRef,
                    });
                } else {
                    dispatchRenderStatus(dispatch, model.id, getInactiveRenderStatus(asset));
                }
                return;
            }

            if (asset.geometryStatus === 'failed') {
                dispatchRenderStatus(dispatch, model.id, 'failed');
                return;
            }

            if (asset.geometryStatus !== 'ready' || asset.textureStatus === 'loading') {
                dispatchRenderStatus(dispatch, model.id, 'loading');
                return;
            }

            const currentEntry = renderHandlesRef.current.get(model.id);

            if (syncExistingRenderable(model, asset, currentEntry)) {
                return;
            }

            startRenderableBuild(model, asset, buildVersion, isCancelled);
        };

        pruneInactiveRenderHandles();

        const buildVersion = buildVersionRef.current + 1;
        buildVersionRef.current = buildVersion;
        let cancelled = false;
        const isCancelled = () => cancelled;

        sceneModels.forEach((model) => {
            reconcileModelRenderable(model, buildVersion, isCancelled);
        });

        return () => {
            cancelled = true;
            buildVersionRef.current += 1;
        };
    }, [assetsById, isViewportReady, sceneModels, stageRef, viewerBinaryCache, visibleModelIdSet]);

    return {
        buildableSceneModels,
        isSceneLoading,
        renderHandlesRef,
        renderStatuses,
        runtimeModelStateById,
        visibleBoundsVersion,
    };
}

export default useSceneRenderables;
